/**
 * Credit Control Dashboard V2 - Exact Screenshot Replication
 * 
 * Based on user research and visual design specifications
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  RefreshCw,
  Sparkles,
  User,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Info,
  TrendingDown,
  TrendingUp,
  Calendar,
  Target,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDashboardData } from '@/hooks/useDashboardData';

export function CreditControlDashboardV2() {
  const [selectedFilter, setSelectedFilter] = useState('everyone');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [strategyTab, setStrategyTab] = useState<'all' | 'active' | 'paused'>('all');

  const {
    todaysPriorities,
    agingPortfolio,
    performanceSnapshot,
    riskAlerts,
    collectionStrategies,
    aiInsights,
    filterOptions,
    refreshData,
  } = useDashboardData(selectedFilter);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshData();
    setIsRefreshing(false);
  };

  const handleFilterChange = (filterId: string) => {
    setSelectedFilter(filterId);
    localStorage.setItem('dashboardFilter', filterId);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getFilterLabel = () => {
    if (selectedFilter === 'everyone') return 'Everyone';
    const option = filterOptions.find(f => f.id === selectedFilter);
    return option?.name || 'Everyone';
  };

  const filteredStrategies = collectionStrategies.strategies.filter(strategy => {
    if (strategyTab === 'all') return true;
    if (strategyTab === 'active') return strategy.status === 'active';
    if (strategyTab === 'paused') return strategy.status === 'paused';
    return true;
  });

  // Mock data for Week Ahead
  const weekAhead = [
    { day: 'Mon', promises: 0, amount: 0 },
    { day: 'Tue', promises: 0, amount: 0 },
    { day: 'Wed', promises: 1, amount: 23404 },
    { day: 'Thu', promises: 0, amount: 0 },
    { day: 'Fri', promises: 0, amount: 0 },
  ];

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      {/* HEADER CONTROLS */}
      <div className="flex items-center justify-between gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>

        <div className="flex items-center gap-3">
          <Button
            className="gap-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white"
            size="sm"
          >
            <Sparkles className="h-4 w-4" />
            AI Insights
            <Badge variant="secondary" className="ml-1 bg-white/90 text-purple-700 hover:bg-white/90">
              {aiInsights.confidence}% confidence
            </Badge>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <User className="h-4 w-4" />
                {getFilterLabel()}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onClick={() => handleFilterChange('everyone')}
                className="gap-2"
              >
                <User className="h-4 w-4" />
                Everyone
                {selectedFilter === 'everyone' && <CheckCircle2 className="h-4 w-4 ml-auto text-green-600" />}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {filterOptions.map((option) => (
                <DropdownMenuItem
                  key={option.id}
                  onClick={() => handleFilterChange(option.id)}
                  className="gap-2"
                >
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-xs font-medium">
                    {option.initials}
                  </div>
                  {option.name}
                  {selectedFilter === option.id && <CheckCircle2 className="h-4 w-4 ml-auto text-green-600" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* PAGE HEADER */}
      <div>
        <h1 className="text-3xl font-bold">Credit Control Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {new Date().toLocaleDateString("en-GB", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </p>
      </div>

      {/* TODAY'S PRIORITIES */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Target className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Today's Priorities</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Collection Queue */}
          <Link to="/collections/queue?filter=today">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="pt-6">
                <div className="space-y-1">
                  <p className="text-xs uppercase font-medium text-muted-foreground tracking-wide">
                    COLLECTION QUEUE
                  </p>
                  <p className="text-5xl font-bold mt-2">
                    {todaysPriorities.collectionQueue.count}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    accounts requiring action
                  </p>
                  <p className="text-lg font-semibold mt-3">
                    {formatCurrency(todaysPriorities.collectionQueue.amount)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Unallocated Payments */}
          <Link to="/payments/allocate?status=unallocated">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="pt-6">
                <div className="space-y-1">
                  <p className="text-xs uppercase font-medium text-muted-foreground tracking-wide">
                    UNALLOCATED PAYMENTS
                  </p>
                  <p className="text-5xl font-bold mt-2">
                    {todaysPriorities.unallocatedPayments.count}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    receipts pending
                  </p>
                  <p className="text-lg font-semibold mt-3">
                    {formatCurrency(todaysPriorities.unallocatedPayments.amount)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Promises Due Today */}
          <Link to="/collections/promises?filter=due-and-broken">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="pt-6">
                <div className="space-y-1">
                  <p className="text-xs uppercase font-medium text-muted-foreground tracking-wide">
                    PROMISES DUE TODAY
                  </p>
                  <p className="text-5xl font-bold mt-2">
                    {todaysPriorities.promisesDueToday.count}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    due today
                  </p>
                  <p className="text-lg font-semibold mt-3">
                    {formatCurrency(todaysPriorities.promisesDueToday.amount)}
                    {todaysPriorities.promisesDueToday.brokenCount > 0 && (
                      <span className="text-sm font-normal text-muted-foreground ml-1">
                        | {todaysPriorities.promisesDueToday.brokenCount} broken
                      </span>
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Disputes */}
          <Link to="/disputes?status=requiring-response">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="pt-6">
                <div className="space-y-1">
                  <p className="text-xs uppercase font-medium text-muted-foreground tracking-wide">
                    DISPUTES
                  </p>
                  <p className="text-5xl font-bold mt-2">
                    {todaysPriorities.disputes.count}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    requiring response
                  </p>
                  <p className="text-lg font-semibold mt-3">
                    {formatCurrency(todaysPriorities.disputes.amount)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>

      {/* ROW 2: AGING PORTFOLIO + PERFORMANCE SNAPSHOT */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Aging Portfolio */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Aging Portfolio</CardTitle>
            <CardDescription className="text-sm">
              {formatCurrency(agingPortfolio.totalOutstanding)} total outstanding
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {agingPortfolio.bands.map((band, index) => (
              <Link
                key={index}
                to={`/analytics-reporting/ageing-analysis?filter=${band.filterKey}`}
                className="block"
              >
                <div className="space-y-2 hover:bg-accent/30 p-2 -mx-2 rounded-lg transition-colors cursor-pointer">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{band.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold">
                        {formatCurrency(band.amount)}
                      </span>
                      <span className="text-muted-foreground">
                        ({band.percentage}%)
                      </span>
                    </div>
                  </div>
                  <Progress value={band.percentage} className="h-2 bg-gray-200" />
                </div>
              </Link>
            ))}
            <div className="pt-2">
              <Link to="/analytics-reporting/ageing-analysis" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                See your aging debt <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Performance Snapshot */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              <CardTitle className="text-lg">Performance Snapshot</CardTitle>
            </div>
            <CardDescription className="text-sm">Your achievements today</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Today's Wins */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                Today's Wins
              </h3>
              <div className="space-y-0.5">
                <Link to="/payments/receipts?filter=today" className="block">
                  <div className="flex items-center justify-between py-2 px-2 -mx-2 hover:bg-accent/30 rounded-lg transition-colors cursor-pointer">
                    <span className="text-sm text-muted-foreground">Payments Received</span>
                    <div className="flex items-center gap-1">
                      <span className="text-lg font-semibold">
                        {performanceSnapshot.todaysWins.paymentsReceived.count} • £{(performanceSnapshot.todaysWins.paymentsReceived.amount / 1000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </Link>
                <Link to="/collections/promises?filter=kept-today" className="block">
                  <div className="flex items-center justify-between py-2 px-2 -mx-2 hover:bg-accent/30 rounded-lg transition-colors cursor-pointer">
                    <span className="text-sm text-muted-foreground">Promises Kept</span>
                    <div className="flex items-center gap-1">
                      <span className="text-lg font-semibold">
                        {performanceSnapshot.todaysWins.promisesKept}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </Link>
                <Link to="/collections/queue?filter=resolved-today" className="block">
                  <div className="flex items-center justify-between py-2 px-2 -mx-2 hover:bg-accent/30 rounded-lg transition-colors cursor-pointer">
                    <span className="text-sm text-muted-foreground">Cases Resolved</span>
                    <div className="flex items-center gap-1">
                      <span className="text-lg font-semibold">
                        {performanceSnapshot.todaysWins.casesResolved}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>

            {/* Month-to-Date Performance */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-semibold">Month-to-Date Performance</h3>

              {/* Collection Rate */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Collection Rate</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <div className="space-y-2 text-xs">
                            <p className="font-semibold">Collection Rate Calculation:</p>
                            <p>Cash collected this month ÷ Total invoiced due this month</p>
                            <p>
                              {formatCurrency(performanceSnapshot.monthToDate.collectionRate.collectedAmount)} ÷{' '}
                              {formatCurrency(performanceSnapshot.monthToDate.collectionRate.totalDueAmount)} ={' '}
                              {performanceSnapshot.monthToDate.collectionRate.current}%
                            </p>
                            <p className="mt-2">
                              Target: {performanceSnapshot.monthToDate.collectionRate.target}% (industry benchmark)
                            </p>
                            <p>
                              Current gap: {performanceSnapshot.monthToDate.collectionRate.pointsBelowTarget} percentage points below target
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <span className="text-3xl font-bold">
                    {performanceSnapshot.monthToDate.collectionRate.current}%
                  </span>
                </div>
                <Progress
                  value={performanceSnapshot.monthToDate.collectionRate.current}
                  className="h-2"
                />
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">
                    Target: {performanceSnapshot.monthToDate.collectionRate.target}%
                  </span>
                  <span className="text-orange-600 flex items-center gap-1">
                    • {performanceSnapshot.monthToDate.collectionRate.pointsBelowTarget} points below target
                    <AlertTriangle className="h-3 w-3" />
                  </span>
                </div>
              </div>

              {/* Key Metrics */}
              <div>
                <h4 className="text-sm font-semibold mb-3">Key Metrics</h4>
                <div className="grid grid-cols-3 gap-4">
                  {/* DSO */}
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">DSO</p>
                    <p className="text-xl font-bold">
                      {performanceSnapshot.monthToDate.keyMetrics.dso.value}
                    </p>
                    <p className="text-xs text-muted-foreground">days</p>
                    <p className="text-xs text-orange-600 flex items-center gap-1 mt-1">
                      Target: {performanceSnapshot.monthToDate.keyMetrics.dso.target}
                      <AlertTriangle className="h-3 w-3" />
                    </p>
                  </div>

                  {/* Promise Success */}
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Promise Success</p>
                    <p className="text-xl font-bold">
                      {performanceSnapshot.monthToDate.keyMetrics.promiseSuccess.percentage}%
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {performanceSnapshot.monthToDate.keyMetrics.promiseSuccess.kept} kept of{' '}
                      {performanceSnapshot.monthToDate.keyMetrics.promiseSuccess.total} total (this month)
                    </p>
                  </div>

                  {/* Avg Payment Days */}
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Avg Payment Days</p>
                    <p className="text-xl font-bold">
                      {performanceSnapshot.monthToDate.keyMetrics.avgPaymentDays.current}
                    </p>
                    <p className="text-xs text-muted-foreground">days</p>
                    <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <TrendingDown className="h-3 w-3" />
                      {performanceSnapshot.monthToDate.keyMetrics.avgPaymentDays.trend.change} days faster than last month
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ROW 3: RISK ALERTS + COLLECTION STRATEGY */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Alerts */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              <CardTitle className="text-lg">Risk Alerts</CardTitle>
            </div>
            <CardDescription className="text-xs">
              Customer-specific risks requiring investigation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {riskAlerts.map((alert) => (
                <Link key={alert.id} to={alert.clickUrl} className="block">
                  <div className={`relative pl-4 pr-3 py-3 rounded-lg border transition-all hover:shadow-sm cursor-pointer ${
                    alert.severity === 'urgent' 
                      ? 'bg-red-50/50 border-red-100' 
                      : alert.severity === 'warning' 
                      ? 'bg-orange-50/50 border-orange-100' 
                      : 'bg-blue-50/50 border-blue-100'
                  }`}>
                    {/* Left border indicator */}
                    <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${
                      alert.severity === 'urgent' 
                        ? 'bg-red-500' 
                        : alert.severity === 'warning' 
                        ? 'bg-orange-500' 
                        : 'bg-blue-500'
                    }`} />
                    
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm mb-0.5 flex items-center gap-2">
                          {alert.type === 'strategy-inactive' && (
                            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                          )}
                          <span className="truncate">
                            {alert.type === 'strategy-inactive' ? 'Collection Strategy Inactive' : alert.customerName}
                          </span>
                        </p>
                        <p className="text-sm text-gray-700 mb-0.5">
                          {alert.type === 'strategy-inactive' ? alert.primaryIssue : alert.primaryIssue}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {alert.context}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Collection Strategy */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Collection Strategy</CardTitle>
                <CardDescription className="text-xs">
                  Active strategies and performance
                </CardDescription>
              </div>
              <Link to="/settings/strategies" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                See More <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Donut Chart */}
            <div className="flex items-center justify-center py-4">
              <div className="relative w-48 h-48">
                {/* SVG Donut Chart */}
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  {/* Background circle */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="12"
                  />
                  {/* Teal segment (Standard 30-Day) - 31% */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#14b8a6"
                    strokeWidth="12"
                    strokeDasharray="78 251"
                    strokeDashoffset="0"
                  />
                  {/* Red segment (Final Notice) - 49% */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="12"
                    strokeDasharray="123 251"
                    strokeDashoffset="-78"
                  />
                  {/* Orange segment (High-Risk) - 20% */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth="12"
                    strokeDasharray="50 251"
                    strokeDashoffset="-201"
                  />
                </svg>
                {/* Center text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-2xl font-bold">£926K</p>
                  <p className="text-xs text-muted-foreground text-center mt-1">
                    Total in Active<br />Collection
                  </p>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center gap-6 border-b">
              <button
                onClick={() => setStrategyTab('all')}
                className={`pb-2 text-sm font-medium transition-colors relative ${
                  strategyTab === 'all'
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                All
                {strategyTab === 'all' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
              <button
                onClick={() => setStrategyTab('active')}
                className={`pb-2 text-sm font-medium transition-colors relative ${
                  strategyTab === 'active'
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Active
                {strategyTab === 'active' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
              <button
                onClick={() => setStrategyTab('paused')}
                className={`pb-2 text-sm font-medium transition-colors relative ${
                  strategyTab === 'paused'
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Paused
                {strategyTab === 'paused' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            </div>

            {/* Strategy List */}
            <div className="space-y-1">
              {filteredStrategies.map((strategy) => (
                <Link key={strategy.id} to={`/settings/strategies/${strategy.id}`} className="block">
                  <div className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-accent/30 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Badge
                        variant={strategy.status === 'active' ? 'default' : 'secondary'}
                        className={`text-xs font-medium ${
                          strategy.status === 'active'
                            ? 'bg-green-100 text-green-700 hover:bg-green-100'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {strategy.status.toUpperCase()}
                      </Badge>
                      <span className="font-medium text-sm truncate">{strategy.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="font-semibold text-sm">
                        {formatCurrency(strategy.amountInCollection)}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ROW 4: WEEK AHEAD + AI INSIGHTS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Week Ahead */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Week Ahead</CardTitle>
            </div>
            <CardDescription className="text-xs">
              Upcoming promises and scheduled actions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {weekAhead.map((day, index) => (
                <Link 
                  key={index} 
                  to={`/collections/promises?day=${day.day.toLowerCase()}`}
                  className="block"
                >
                  <div className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-accent/30 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-sm w-10">{day.day}</span>
                      <span className="text-sm text-muted-foreground">
                        {day.promises} promise{day.promises !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">
                        {formatCurrency(day.amount)}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* AI Insights */}
        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                <CardTitle className="text-lg">AI Insights</CardTitle>
              </div>
              <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
                {aiInsights.confidence}% confidence
              </Badge>
            </div>
            <CardDescription className="text-xs text-purple-900/70">
              Machine learning recommendations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Top Priority Collection */}
            <Card className="border-purple-200 bg-white">
              <CardContent className="pt-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm mb-1">Top Priority Collection</h4>
                      <Link to="/customers/manufacturing-co" className="text-sm text-primary hover:underline">
                        Manufacturing Co Ltd <ChevronRight className="h-3 w-3 inline" />
                      </Link>
                      <p className="text-xs text-muted-foreground mt-1">
                        High payment probability based on historical patterns
                      </p>
                    </div>
                  </div>
                  <Button className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white">
                    Initiate Collection Call
                  </Button>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Expected Recovery</span>
                    <span className="font-semibold">£505,000</span>
                  </div>
                  <Progress value={85} className="h-1.5" />
                </div>
              </CardContent>
            </Card>

            {/* Optimization Tip */}
            <Card className="border-blue-200 bg-white">
              <CardContent className="pt-4">
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <Target className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm mb-1">Optimization Tip</h4>
                      <p className="text-xs text-muted-foreground">
                        Focus on customers with 60-90 day aging for best recovery rates this week
                      </p>
                      <Link to="/analytics-reporting/ageing-analysis" className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-2">
                        View aging analysis <ChevronRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
